import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/payments/payments/`;
const CHEQUES_URL = `${process.env.REACT_APP_API_BASE_URL}/api/payments/with-cheques/`;

// --- Thunks --- //

const toFinanceRow = (payment) => ({
  ...payment,
  category: 'صادر',
  targetName:
    payment.target_name ||
    payment.recipient_employee?.full_name ||
    payment.recipient_bus?.name ||
    payment.recipient_authorized?.display_value ||
    'غير معروف',
});

const toParams = (filters) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  });
  return params;
};

// Resolves to { results, count }. Pass `page` (0-indexed) + `pageSize` for one
// server page; without `page` the full unpaginated list is fetched, matching
// this thunk's behavior before pagination was added.
export const fetchFinance = createAsyncThunk(
  'finance/fetchFinance',
  async (filters = {}, thunkAPI) => {
    try {
      const { page, pageSize, ...rest } = filters;
      const params = toParams(rest);

      if (page !== undefined) {
        // DRF PageNumberPagination is 1-indexed.
        params.append('page', page + 1);
        params.append('page_size', pageSize || 25);
      } else {
        params.append('page_size', 'all');
      }

      const response = await axios.get(`${BASE_URL}?${params.toString()}`);
      const data = response.data;
      const rows = Array.isArray(data) ? data : data.results;
      return {
        results: rows.map(toFinanceRow),
        count: Array.isArray(data) ? data.length : data.count,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Fetch failed');
    }
  }
);

// Every row matching the given filters in one unpaginated call - used by
// Export/Print, which need the full filtered result set, not one page.
export const fetchFinanceForExport = createAsyncThunk(
  'finance/fetchForExport',
  async (filters = {}, thunkAPI) => {
    try {
      const params = toParams(filters);
      params.append('page_size', 'all');
      const response = await axios.get(`${BASE_URL}?${params.toString()}`);
      const data = response.data;
      return (Array.isArray(data) ? data : data.results).map(toFinanceRow);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Export fetch failed');
    }
  }
);

// Server-computed { count, total_amount } for the current filters, so summary
// cards stay accurate without fetching every row just to sum them client-side.
export const fetchFinanceSummary = createAsyncThunk(
  'finance/fetchSummary',
  async (filters = {}, thunkAPI) => {
    try {
      const response = await axios.get(`${BASE_URL}summary/?${toParams(filters).toString()}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Summary fetch failed');
    }
  }
);

// NEW: Fetch payments with cheques
export const fetchPaymentsWithCheques = createAsyncThunk(
  'finance/fetchPaymentsWithCheques',
  async (filters = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();

      // Add filters to URLSearchParams
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${CHEQUES_URL}?${params.toString()}`);

      // Handle both array response and object with results
      const paymentsData = Array.isArray(response.data) 
        ? response.data 
        : response.data.results || [];

      return {
        payments: paymentsData.map((payment) => ({
          ...payment,
          category: 'صادر',
          targetName: payment.target_name || 'غير معروف',
          // Format cheque data for display
          chequeInfo: payment.cheque ? {
            number: payment.cheque.cheque_number,
            bankNumber: payment.cheque.bank_number,
            branchNumber: payment.cheque.branch_number,
            accountNumber: payment.cheque.account_number,
            date: payment.cheque.cheque_date,
            hasImage: payment.cheque.has_image || !!payment.cheque.cheque_image_url,
            imageUrl: payment.cheque.cheque_image_url,
            description: payment.cheque.description
          } : null
        })),
        summary: response.data.summary || {
          total_payments: paymentsData.length,
          total_amount: 0,
          cheques_with_images: 0,
          cheques_without_images: 0
        }
      };
    } catch (error) {
      console.error('Fetch payments with cheques error:', error);
      return thunkAPI.rejectWithValue(error.response?.data || 'Fetch cheques failed');
    }
  }
);

export const createPayment = createAsyncThunk(
  'finance/createPayment', 
  async (payment, thunkAPI) => {
    try {
      const formData = new FormData();

      // Core fields
      formData.append('receive_id', payment.receiptNumber);
      formData.append('type', payment.entityType);
      formData.append('amount', payment.amount);
      formData.append('reason', payment.reason);
      formData.append('school_year', payment.school_year);
      formData.append('date', payment.paymentDate || new Date().toISOString().split('T')[0]);

      // Set Payment Type (method like 'شيك' maps to a PaymentType)
      formData.append('payment_type', payment.method);

      // Set authorized payer (optional)
      if (payment.authorizedPayerId) {
        formData.append('authorized_payer', payment.authorizedPayerId);
      }

      // Set the correct recipient field based on entity type
      if (payment.entityType === 'موظف') {
        formData.append('recipient_employee', payment.targetId);
      } else if (payment.entityType === 'باص') {
        formData.append('recipient_bus', payment.targetId);
      } else if (payment.entityType === 'جهة صرف معتمدة') {
        formData.append('recipient_authorized', payment.targetId);
      }

      // ✅ Handle Cheque Details
      if (payment.method === 'شيك' && payment.chequeDetails) {
        const { chequeDetails } = payment;
        
        if (chequeDetails.bankNumber) {
          formData.append('cheque_details.bank_number', chequeDetails.bankNumber);
        }
        if (chequeDetails.branchNumber) {
          formData.append('cheque_details.branch_number', chequeDetails.branchNumber);
        }
        if (chequeDetails.bankName) {
          formData.append('cheque_details.bank_name', chequeDetails.bankName);
        }
        if (chequeDetails.branchName) {
          formData.append('cheque_details.branch_name', chequeDetails.branchName);
        }
        if (chequeDetails.countryCode) {
          formData.append('cheque_details.country_code', chequeDetails.countryCode);
        }
        if (chequeDetails.accountNumber) {
          formData.append('cheque_details.account_number', chequeDetails.accountNumber);
        }
        if (chequeDetails.dueDate) {
          formData.append('cheque_details.cheque_date', chequeDetails.dueDate);
        }
        if (chequeDetails.chequeNumber) {
          formData.append('cheque_details.cheque_number', chequeDetails.chequeNumber);
        }
        if (chequeDetails.description || chequeDetails.note) {
          formData.append('cheque_details.description', chequeDetails.description || chequeDetails.note);
        }

        // Handle cheque image
        if (chequeDetails.chequeImage) {
          formData.append('cheque_details.cheque_image', chequeDetails.chequeImage);
        }
      }

      // ✅ NEW: Handle Visa Details
      if (payment.method === 'فيزا' && payment.visaDetails) {
        const { visaDetails } = payment;
        
        if (visaDetails.receiptNumber) {
          formData.append('visa_details.receipt_number', visaDetails.receiptNumber);
        }
        if (visaDetails.notes) {
          formData.append('visa_details.notes', visaDetails.notes);
        }
        
        // If you have other visa-specific fields, add them here
        // For example:
        // if (visaDetails.transactionId) {
        //   formData.append('visa_details.transaction_id', visaDetails.transactionId);
        // }
      }

      const response = await axios.post(BASE_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        ...response.data,
        category: 'صادر',
        targetName: response.data.target_name || 
                   response.data.recipient_employee?.full_name ||
                   response.data.recipient_bus?.name ||
                   response.data.recipient_authorized?.display_value ||
                   'غير معروف',
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Payment creation failed');
    }
  }
);

export const updatePayment = createAsyncThunk(
  'finance/updatePayment', 
  async (updatedPayment, thunkAPI) => {
    try {
      const formData = new FormData();

      // Core fields
      if (updatedPayment.receiptNumber) formData.append('receive_id', updatedPayment.receiptNumber);
      if (updatedPayment.entityType) formData.append('type', updatedPayment.entityType);
      if (updatedPayment.amount) formData.append('amount', updatedPayment.amount);
      if (updatedPayment.reason) formData.append('reason', updatedPayment.reason);
      if (updatedPayment.school_year) formData.append('school_year', updatedPayment.school_year);
      if (updatedPayment.paymentDate) formData.append('date', updatedPayment.paymentDate);

      // Payment method
      if (updatedPayment.method) formData.append('payment_type', updatedPayment.method);

      // Set the correct recipient field based on entity type
      if (updatedPayment.entityType === 'موظف' && updatedPayment.targetId) {
        formData.append('recipient_employee', updatedPayment.targetId);
      } else if (updatedPayment.entityType === 'باص' && updatedPayment.targetId) {
        formData.append('recipient_bus', updatedPayment.targetId);
      } else if (updatedPayment.entityType === 'جهة صرف معتمدة' && updatedPayment.targetId) {
        formData.append('recipient_authorized', updatedPayment.targetId);
      }

      // Handle Cheque Details
      if (updatedPayment.method === 'شيك' && updatedPayment.chequeDetails) {
        const { chequeDetails } = updatedPayment;
        if (chequeDetails.bankNumber) formData.append('cheque_details.bank_number', chequeDetails.bankNumber);
        if (chequeDetails.branchNumber) formData.append('cheque_details.branch_number', chequeDetails.branchNumber);
        if (chequeDetails.bankName) formData.append('cheque_details.bank_name', chequeDetails.bankName);
        if (chequeDetails.branchName) formData.append('cheque_details.branch_name', chequeDetails.branchName);
        if (chequeDetails.countryCode) formData.append('cheque_details.country_code', chequeDetails.countryCode);
        if (chequeDetails.accountNumber) formData.append('cheque_details.account_number', chequeDetails.accountNumber);
        if (chequeDetails.dueDate) formData.append('cheque_details.cheque_date', chequeDetails.dueDate);
        if (chequeDetails.chequeNumber) formData.append('cheque_details.cheque_number', chequeDetails.chequeNumber);
        if (chequeDetails.description) formData.append('cheque_details.description', chequeDetails.description);
        if (chequeDetails.chequeImage) formData.append('cheque_details.cheque_image', chequeDetails.chequeImage);
      }

      // Handle Visa Details
      if (updatedPayment.method === 'فيزا' && updatedPayment.visaDetails) {
        const { visaDetails } = updatedPayment;
        if (visaDetails.receiptNumber) formData.append('visa_details.receipt_number', visaDetails.receiptNumber);
        if (visaDetails.notes) formData.append('visa_details.notes', visaDetails.notes);
      }

      // Handle proof attachment
      if (updatedPayment.proofAttachment) {
        formData.append('proof_attachment', updatedPayment.proofAttachment);
      }

      const response = await axios.patch(`${BASE_URL}${updatedPayment.id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        ...response.data,
        category: 'صادر',
        targetName: response.data.target_name ||
                   response.data.recipient_employee?.full_name || 
                   response.data.recipient_bus?.name ||
                   response.data.recipient_authorized?.display_value ||
                   'غير معروف',
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Update failed');
    }
  }
);

export const fetchPaymentById = createAsyncThunk(
  'finance/fetchPaymentById', 
  async (id, thunkAPI) => {
    try {
      const response = await axios.get(`${BASE_URL}${id}/`);
      return {
        ...response.data,
        category: 'صادر',
        targetName: response.data.target_name ||
                   response.data.recipient_employee?.full_name || 
                   response.data.recipient_authorized?.full_name || 
                   'غير معروف',
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Fetch failed');
    }
  }
);

export const deletePayment = createAsyncThunk(
  'payments/delete', 
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${BASE_URL}${id}/`);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Delete failed');
    }
  }
);

// NEW: Delete cheque image
export const deleteChequeImage = createAsyncThunk(
  'finance/deleteChequeImage',
  async (chequeId, thunkAPI) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/payments/cheques/${chequeId}/delete-image/`
      );
      return { chequeId, message: response.data.message };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Delete image failed');
    }
  }
);

// --- Slice --- //

const financeSlice = createSlice({
  name: 'finance',
  initialState: {
    data: [],
    filteredData: [],
    count: 0,
    summary: { count: 0, total_amount: 0 },
    selectedPayment: null,
    // NEW: Cheques specific state
    chequesData: [],
    chequesSummary: {
      total_payments: 0,
      total_amount: 0,
      cheques_with_images: 0,
      cheques_without_images: 0
    },
    filters: {
      searchQuery: '',
      type: '',
      status: '',
      method: '',
      category: '',
      // NEW: Cheque specific filters
      chequeStatus: '', // 'with_image', 'without_image', 'all'
      startDate: '',
      endDate: '',
      schoolYear: '',
    },
    status: 'idle',
    chequesStatus: 'idle', // NEW: separate status for cheques
    error: null,
  },
  reducers: {
    setSearchQuery: (state, action) => {
      state.filters.searchQuery = action.payload;
      financeSlice.caseReducers.applyFilters(state);
    },
    setTypeFilter: (state, action) => {
      state.filters.type = action.payload;
      financeSlice.caseReducers.applyFilters(state);
    },
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
      financeSlice.caseReducers.applyFilters(state);
    },
    setMethodFilter: (state, action) => {
      state.filters.method = action.payload;
      financeSlice.caseReducers.applyFilters(state);
    },
    setCategoryFilter: (state, action) => {
      state.filters.category = action.payload;
      financeSlice.caseReducers.applyFilters(state);
    },
    // NEW: Cheque specific filter setters
    setChequeStatusFilter: (state, action) => {
      state.filters.chequeStatus = action.payload;
      financeSlice.caseReducers.applyChequeFilters(state);
    },
    setDateRangeFilter: (state, action) => {
      state.filters.startDate = action.payload.startDate || '';
      state.filters.endDate = action.payload.endDate || '';
      financeSlice.caseReducers.applyChequeFilters(state);
    },
    setSchoolYearFilter: (state, action) => {
      state.filters.schoolYear = action.payload;
      financeSlice.caseReducers.applyChequeFilters(state);
    },
    applyFilters: (state) => {
      let updated = [...state.data];
      const { searchQuery, type, status, method, category } = state.filters;

      if (searchQuery)
        updated = updated.filter((f) => f.targetName?.includes(searchQuery));

      if (type) updated = updated.filter((f) => f.type === type);
      if (status) updated = updated.filter((f) => f.status === status);
      if (method) updated = updated.filter((f) => f.method === method);
      if (category) updated = updated.filter((f) => f.category === category);

      state.filteredData = updated;
    },
    // NEW: Apply filters for cheques data
    applyChequeFilters: (state) => {
      // This could be used for client-side filtering if needed
      // For now, we'll rely on server-side filtering via API params
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Original finance actions
      .addCase(fetchFinance.pending, (state) => {
        state.status = 'loading';
        state.data = [];
        state.filteredData = [];
        state.error = null;
      })
      .addCase(fetchFinance.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload.results;
        state.filteredData = action.payload.results;
        state.count = action.payload.count;
        state.error = null;
      })
      .addCase(fetchFinanceSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
      })
      .addCase(fetchFinance.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      
      // NEW: Payments with cheques actions
      .addCase(fetchPaymentsWithCheques.pending, (state) => {
        state.chequesStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchPaymentsWithCheques.fulfilled, (state, action) => {
        state.chequesStatus = 'succeeded';
        state.chequesData = action.payload.payments;
        state.chequesSummary = action.payload.summary;
        state.error = null;
      })
      .addCase(fetchPaymentsWithCheques.rejected, (state, action) => {
        state.chequesStatus = 'failed';
        state.error = action.payload || action.error.message;
      })
      
      .addCase(createPayment.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data.push(action.payload);
        state.filteredData.push(action.payload);
        
        // NEW: If the created payment has a cheque, add it to chequesData too
        if (action.payload.cheque) {
          state.chequesData.push(action.payload);
        }
        
        state.error = null;
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(updatePayment.fulfilled, (state, action) => {
        const index = state.data.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) state.data[index] = action.payload;

        const filteredIndex = state.filteredData.findIndex((p) => p.id === action.payload.id);
        if (filteredIndex !== -1) state.filteredData[filteredIndex] = action.payload;

        // NEW: Update in chequesData if it exists there
        const chequeIndex = state.chequesData.findIndex((p) => p.id === action.payload.id);
        if (chequeIndex !== -1) state.chequesData[chequeIndex] = action.payload;

        if (state.selectedPayment?.id === action.payload.id) {
          state.selectedPayment = action.payload;
        }
      })
      .addCase(fetchPaymentById.fulfilled, (state, action) => {
        state.selectedPayment = action.payload;
      })
      .addCase(fetchPaymentById.rejected, (state, action) => {
        state.selectedPayment = null;
        state.error = action.payload || action.error.message;
      })
      .addCase(deletePayment.fulfilled, (state, action) => {
        state.data = state.data.filter((p) => p.id !== action.payload);
        state.filteredData = state.filteredData.filter((p) => p.id !== action.payload);
        // NEW: Remove from chequesData too
        state.chequesData = state.chequesData.filter((p) => p.id !== action.payload);
      })
      .addCase(deletePayment.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      
      // NEW: Delete cheque image actions
      .addCase(deleteChequeImage.fulfilled, (state, action) => {
        // Update the cheque data to show image was deleted
        const { chequeId } = action.payload;
        
        // Update in chequesData
        const chequeIndex = state.chequesData.findIndex((p) => p.cheque?.id === chequeId);
        if (chequeIndex !== -1 && state.chequesData[chequeIndex].cheque) {
          state.chequesData[chequeIndex].cheque.hasImage = false;
          state.chequesData[chequeIndex].cheque.imageUrl = null;
        }
        
        // Update in regular data if it exists
        const dataIndex = state.data.findIndex((p) => p.cheque?.id === chequeId);
        if (dataIndex !== -1 && state.data[dataIndex].cheque) {
          state.data[dataIndex].cheque.hasImage = false;
          state.data[dataIndex].cheque.imageUrl = null;
        }
      })
      .addCase(deleteChequeImage.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      });
  },
});

export const {
  setSearchQuery,
  setTypeFilter,
  setStatusFilter,
  setMethodFilter,
  setCategoryFilter,
  setChequeStatusFilter,
  setDateRangeFilter,
  setSchoolYearFilter,
  applyFilters,
  applyChequeFilters,
  clearError,
} = financeSlice.actions;

export default financeSlice.reducer;